import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createImageEngine,
  defaultCorners,
  releaseCanvas,
  rotateQuarterTurns,
  type Corners,
  type ImageEngine,
  type RenderParams,
} from '@/tools/image-engine';

/** The Immagini tool keeps more detail than identity documents (2000 px): text must stay legible. */
export const MAX_SOURCE_LONG_EDGE = 3000;

export interface LoadedImage {
  readonly name: string;
  readonly bytes: number;
  readonly source: HTMLCanvasElement;
}

/** Decodes `file` into a canvas whose long edge is at most `MAX_SOURCE_LONG_EDGE`. */
export function decodeImage(file: File): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      const scale = Math.min(
        1,
        MAX_SOURCE_LONG_EDGE / Math.max(image.naturalWidth, image.naturalHeight),
      );
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      URL.revokeObjectURL(url);
      if (!ctx) {
        reject(new Error('2D canvas context unavailable'));
        return;
      }
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable image'));
    };
    image.src = url;
  });
}

/**
 * One photo being worked on: its source, its corners, and the image engine (a Worker where the
 * browser allows it). Rotating by 90° replaces the source and proposes the corners again. The
 * previous source is released once the engine has the new one (iOS canvas memory).
 */
export function useImageSession() {
  const engineRef = useRef<ImageEngine | null>(null);
  const [image, setImage] = useState<LoadedImage | null>(null);
  const [corners, setCorners] = useState<Corners | null>(null);
  const [cornersFound, setCornersFound] = useState<boolean | null>(null);

  const engine = useCallback((): ImageEngine => (engineRef.current ??= createImageEngine()), []);

  const adopt = useCallback(
    async (next: LoadedImage, previous: HTMLCanvasElement | null): Promise<void> => {
      setImage(next);
      setCorners(defaultCorners(next.source.width, next.source.height));
      setCornersFound(null);
      await engine().setSource(next.source);
      if (previous && previous !== next.source) releaseCanvas(previous);
      const found = await engine().detectCorners();
      setCornersFound(found !== null);
      if (found) setCorners(found);
    },
    [engine],
  );

  const load = useCallback(
    async (file: File): Promise<void> => {
      const source = await decodeImage(file);
      await adopt({ name: file.name, bytes: file.size, source }, image?.source ?? null);
    },
    [adopt, image],
  );

  const rotateQuarter = useCallback(
    async (turns: 1 | -1): Promise<void> => {
      if (!image) return;
      const rotated = rotateQuarterTurns(image.source, turns) as HTMLCanvasElement;
      await adopt({ ...image, source: rotated }, image.source);
    },
    [adopt, image],
  );

  const detect = useCallback(async (): Promise<boolean> => {
    const found = await engine().detectCorners();
    setCornersFound(found !== null);
    if (found) setCorners(found);
    return found !== null;
  }, [engine]);

  const reset = useCallback((): void => {
    if (image) releaseCanvas(image.source);
    setImage(null);
    setCorners(null);
    setCornersFound(null);
  }, [image]);

  const exportJpeg = useCallback(
    (params: Omit<RenderParams, 'corners'>): Promise<string | null> =>
      corners ? engine().exportJpeg({ ...params, corners }, 0.92) : Promise.resolve(null),
    [corners, engine],
  );

  useEffect(
    () => () => {
      engineRef.current?.dispose();
      engineRef.current = null;
    },
    [],
  );

  return {
    image,
    corners,
    setCorners,
    cornersFound,
    engine,
    load,
    rotateQuarter,
    detect,
    reset,
    exportJpeg,
  };
}
